import { Button, IconButton, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { DatePickerField, DateTimePickerField, Icons } from 'utoniq-ui'

import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

import { CreateEventValue } from '../EventCreateForm'

type EventInputDateCellProps = {
  setValue: UseFormSetValue<CreateEventValue>
  errors?: FieldErrors<CreateEventValue>
  control: Control<CreateEventValue>
  watch: UseFormWatch<CreateEventValue>
  startedAt: CreateEventValue['startedAt']
  endedAt: CreateEventValue['endedAt']
}

export const EventInputDateCell: Cell<EventInputDateCellProps> = ({
  setValue,
  errors,
  control,
  watch,
  startedAt,
  endedAt,
}) => {
  const { t } = useTranslation('consoleEventCreate')
  const {
    fields,
    append,
    remove: onRemove,
  } = useFieldArray({
    control,
    keyName: '_id',
    name: 'closeDate',
  })

  const disableCloseDate = useMemo(() => {
    if (!startedAt && !endedAt) {
      return true
    }
    if (startedAt && endedAt) {
      return Math.abs(dayjs(startedAt).diff(endedAt, 'd', true)) < 2
    }
    return false
  }, [endedAt, startedAt])

  return (
    <CellFrame sx={{ pb: 8 }}>
      <Typography variant="h5" pb={2}>
        {t('date', '開催日')}
      </Typography>
      <DateTimePickerField
        onChange={date => {
          setValue(
            'startedAt',
            (convertDatePickerValueToDate(date) || '') as never,
            { shouldValidate: true, shouldDirty: true }
          )
        }}
        value={startedAt ? dayjs(startedAt) : null}
        title={t('eventStartedAtTitle')}
        placeholder={t('eventStartedAtPlaceholder')}
        error={errors?.startedAt}
        titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
      />
      <DateTimePickerField
        onChange={date => {
          setValue(
            'endedAt',
            (convertDatePickerValueToDate(date) || '') as never,
            { shouldValidate: true, shouldDirty: true }
          )
        }}
        value={endedAt ? dayjs(endedAt) : null}
        title={t('eventEndedAtTitle')}
        placeholder={t('eventEndedAtPlaceholder')}
        error={errors?.startedAt}
        titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
      />
      <Stack>
        <Typography variant="subtitle1">
          {t('eventCloseDateTitle', '休業日')}
        </Typography>
        {fields.map((field, index) => {
          const closeDate = watch(`closeDate.${index}.date`)
          return (
            <Stack direction="row" key={field._id} alignItems="center">
              <Stack flex={1}>
                <DatePickerField
                  onChange={date => {
                    setValue(`closeDate.${index}.date`, date as never, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }}
                  minDate={startedAt ? dayjs(startedAt).add(1, 'd') : undefined}
                  maxDate={
                    endedAt ? dayjs(endedAt).subtract(1, 'd') : undefined
                  }
                  value={closeDate ? dayjs(closeDate) : null}
                  textFieldProps={{
                    inputProps: {
                      value: closeDate
                        ? dayjs(closeDate).format('YYYY/MM/DD')
                        : '',
                    },
                  }}
                  placeholder={t('eventCloseDatePlaceholder')}
                  error={errors?.closeDate?.[index]?.date}
                  titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
                />
              </Stack>
              {(!disableCloseDate || closeDate) && (
                <Stack alignItems="center" mb={3}>
                  <IconButton size="small" onClick={() => onRemove(index)}>
                    <Icons.Trash />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          )
        })}
        <Stack alignItems="center">
          <Button
            size="small"
            disabled={disableCloseDate}
            onClick={() => append({ date: null })}
          >
            {t('addCloseDate', '休業日を追加する')}
          </Button>
        </Stack>
      </Stack>
    </CellFrame>
  )
}
